/**
 * weightPhotoCopy.ts — the ONE wording of the weight-photo ask, shared by every
 * surface that makes it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: photo at plan selection
 * (CTO wave 3 patient side, 2026-08-27).
 *
 * ⚠️ WHY THIS FILE EXISTS AT ALL — THE ASK IS THE ONLY CONTROL ON WHAT ARRIVES.
 * The evidence gate downstream is PRESENCE-ONLY: orders-service refuses the
 * prescription when there is no photo, and clinic-service stores whatever bytes
 * the signed PUT carried. Nothing in the estate inspects the IMAGE. So the
 * sentence we put in front of the patient is not copy polish — it is the entire
 * mechanism by which the right photo, rather than merely a photo, reaches the
 * clinician. A wrong-but-present photo sails through every control we have.
 *
 * ⚠️ q14 NO LONGER MAKES THIS ASK (clinic #166, 2026-09-04 — weight
 * self-certification). From question set v3 the questionnaire carries a
 * DECLARATION instead, and no patient is asked for an image at sign-up.
 *
 * ⚠️ AND NEITHER DOES THE PLAN STEP (2026-09-04). The ask lived there from
 * wave 3 until the funnel was measured — see `checkout/PlanSelection`'s header.
 * No surface asks a patient for a photo BEFORE they pay any more.
 *
 * THESE STRINGS ARE NOT DEAD, AND THEY ARE NOT STALE COPY. They are still the
 * ask everywhere a photo is genuinely wanted, all of it AFTER the money: the
 * deferred-photo prompt on the thank-you page and the dashboard, and the wording
 * behind the clinician's evidence request. What changed is WHEN we ask, not what
 * a usable photo looks like; the frame below is still the only thing that makes
 * an image evidence rather than a number. The paragraph that follows describes
 * where the wording CAME from, which is history and stays accurate.
 *
 * ⚠️ AND WHY THE WORDING IS NOT WRITTEN HERE FROM SCRATCH. The ask was owned by
 * clinic-service's `src/seed/weightloss.seed.ts` q14 (Reneal Patel, 2026-08-18):
 * the old ask, "a photo of your weight on the scales", was satisfied by a
 * close-up of a number — a screenshot, a stock image or somebody else's scales
 * all pass it — and the starting weight is the input to the BMI gate a
 * prescriber signs against. Asking for the patient AND the reading in one frame
 * is what makes it evidence rather than a number. These constants restate that
 * seed ask; when the seed changes, change them together.
 *
 * ⚠️ THE FACE PROHIBITION IS GONE, DELIBERATELY. Three surfaces used to say
 * "the scale reading only — never a photo of your face", which is the exact
 * OPPOSITE of what the seed asks for and would make a compliant patient send
 * unusable evidence. The only true constraint left is the identity-document one
 * ({@link WEIGHT_PHOTO_NEVER}), which is a real data-minimisation rule (R19: we
 * do not want, and must not store, a passport scan in a photo bucket).
 */

/**
 * The heading every surface leads with. Verbatim from the seed's q14 `prompt` as
 * it stood in question set v2 — the wording the v1/v2 patients read in the
 * questionnaire, so a deferred-photo prompt on their dashboard meets them in the
 * same words. From v3 no questionnaire makes this ask at all (see the ⚠️ note
 * above); this is now the portal's own ask on the surfaces that still make one.
 */
export const WEIGHT_PHOTO_ASK = 'Add a photo of yourself on the scales';

/**
 * The frame, in one sentence: who is in it, what has to be readable, and the
 * three things that make an image worthless as evidence (a screenshot, someone
 * else's picture, an edited image). Condensed from the seed's q14 `helpText` —
 * the seed additionally carries the "you can add this later" line, which is a
 * property of the QUESTION's options rather than of the ask, and each surface
 * says its own version of that where it is true.
 */
export const WEIGHT_PHOTO_HELP =
  'Stand on your scales and take one photo that shows you and the reading together, with the ' +
  'number clearly readable — taken by you on your own device, not a screenshot and not an ' +
  'edited image.';

/**
 * The one constraint that IS true, kept as a footnote on every surface. Short on
 * purpose: a list of prohibitions competes with the ask, and this is the only
 * one that protects anything.
 */
export const WEIGHT_PHOTO_NEVER = 'Never send an identity document.';
